import React from "react"
import { IconTrendingUp } from "@tabler/icons-react"
import { CommandList, CommandEmpty, CommandGroup, CommandItem } from "./base_ui"
import { SearchResultRow } from "./rows"
import type { SearchSelectHandler } from "./rows"
import type { SearchGroup } from "./search_hit"
import { searchPagePath } from "./search_page_path"

export interface SearchDropdownProps {
  // The debounced query the results correspond to (shown in the empty state + "see all" row). Empty before
  // the user has typed a searchable query — the dropdown then shows the trending state.
  query: string
  loading: boolean
  total: number
  groups: SearchGroup[]
  // Curated trending terms for the first-open (no query) state; selecting one runs a live search.
  trending?: string[]
  onSelect: SearchSelectHandler
  // Mirrors onSelect: the DOM event (undefined on keyboard Enter) + the term, so the handler can honor
  // modifier-clicks (open the link) vs plain clicks (run a live search).
  onTrendingSelect?: (event: React.MouseEvent | undefined, term: string) => void
  onSeeAll: () => void
}

// The grouped result list, rendered with base-UI cmdk primitives (CommandGroup/CommandItem) so it comes
// with keyboard navigation, active-item tracking, and a11y roles for free. It must be rendered inside a
// <Command> (GlobalSearchBar provides one; stories wrap it). Before a query is typed it shows a "Trending"
// state; the "see all / view all" row is pinned to the bottom (sticky) so it's always reachable. Pure
// presentation — selection is reported via callbacks.
export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  query,
  loading,
  total,
  groups,
  trending = [],
  onSelect,
  onTrendingSelect,
  onSeeAll,
}) => {
  const hasQuery = query.trim().length > 0
  // Footer: available on the trending state and whenever a query has results (hidden on a no-results query,
  // where "see all" would just open an equally-empty page).
  const showFooter = !hasQuery || total > 0
  let flatIndex = -1

  return (
    <CommandList className="max-h-[600px]">
      {hasQuery ? (
        <>
          {/* cmdk shows Empty only when no items are rendered, which covers both loading and no-results. */}
          <CommandEmpty>
            {loading ? "Searching…" : `No results for “${query}”`}
          </CommandEmpty>

          {groups.map((group) => (
            <CommandGroup key={group.category} heading={group.category}>
              {group.hits.map((hit) => {
                flatIndex += 1
                return (
                  <SearchResultRow
                    key={hit.id ?? hit.path}
                    category={group.category}
                    hit={hit}
                    position={flatIndex}
                    onSelect={onSelect}
                  />
                )
              })}
            </CommandGroup>
          ))}
        </>
      ) : (
        trending.length > 0 && (
          <CommandGroup heading="Trending">
            {trending.map((term) => (
              // Rendered as a real <a href="/search?q=term"> (like the result rows) so hovering previews
              // the URL and modifier-clicks open it in a new tab; plain clicks are intercepted by the
              // handler to run a live search. cmdk's onSelect covers keyboard Enter (no DOM event).
              <CommandItem
                key={term}
                value={`trending-${term}`}
                onSelect={() => onTrendingSelect?.(undefined, term)}
                className="p-0"
              >
                <a
                  href={searchPagePath(term)}
                  tabIndex={-1}
                  onClick={(event) => {
                    event.stopPropagation()
                    onTrendingSelect?.(event, term)
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2"
                >
                  <IconTrendingUp
                    size={16}
                    className="shrink-0 text-secondary-400"
                    aria-hidden="true"
                  />
                  <span className="truncate">{term}</span>
                </a>
              </CommandItem>
            ))}
          </CommandGroup>
        )
      )}

      {showFooter && (
        <CommandGroup className="sticky bottom-0 border-t bg-secondary-50 pt-0">
          <CommandItem
            value="see-all-results"
            onSelect={onSeeAll}
            className="px-4 py-2 font-medium text-muted-foreground"
          >
            {hasQuery ? (
              <>See all results for “{query}” &rarr;</>
            ) : (
              <>Go to search page &rarr;</>
            )}
          </CommandItem>
        </CommandGroup>
      )}
    </CommandList>
  )
}

export default SearchDropdown
